import type { PoseDetector } from '@tensorflow-models/pose-detection';
import type { Point, PoseFrame } from './types';

export type PoseStats = { inferenceFps: number; confidence: number; slow: boolean };

export class PoseEngine {
  private detector: PoseDetector | null = null;
  private stream: MediaStream | null = null;
  private running = false;
  private lastInfer = 0;
  private interval = 50;
  private emaMs = 45;
  private smoothed: Record<string, Point> = {};
  private runId = 0;

  async startCamera(video: HTMLVideoElement) {
    if (!navigator.mediaDevices?.getUserMedia) throw new Error('camera-unavailable');
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: false,
      video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, max: 30 } },
    });
    video.srcObject = this.stream;
    video.playsInline = true;
    video.muted = true;
    await video.play();
  }

  async attach(video: HTMLVideoElement) {
    if (!this.stream) return this.startCamera(video);
    video.srcObject = this.stream; video.playsInline = true; video.muted = true; await video.play();
  }

  async load() {
    if (this.detector) return;
    const [tf, poseDetection] = await Promise.all([
      import('@tensorflow/tfjs-core'),
      import('@tensorflow-models/pose-detection'),
      import('@tensorflow/tfjs-backend-webgl'),
    ]).then(([core, pose]) => [core, pose] as const);
    await tf.setBackend('webgl');
    await tf.ready();
    this.detector = await poseDetection.createDetector(poseDetection.SupportedModels.MoveNet, {
      modelType: poseDetection.movenet.modelType.SINGLEPOSE_LIGHTNING,
      enableSmoothing: true,
      minPoseScore: .2,
    });
  }

  async run(video: HTMLVideoElement, onPose: (frame: PoseFrame | null, stats: PoseStats) => void) {
    await this.load();
    this.running = true;
    const runId = ++this.runId;
    const loop = async (now: number) => {
      if (!this.running || runId !== this.runId) return;
      if (video.readyState >= 2 && now - this.lastInfer >= this.interval) {
        const started = performance.now();
        try {
          const poses = await this.detector!.estimatePoses(video, { flipHorizontal: true });
          const elapsed = performance.now() - started;
          this.emaMs = this.emaMs * .86 + elapsed * .14;
          this.interval = this.emaMs > 80 ? 100 : this.emaMs > 55 ? 66 : 42;
          const pose = poses[0];
          if (!pose) onPose(null, { inferenceFps: 1000 / this.interval, confidence: 0, slow: this.interval >= 100 });
          else {
            const points: Record<string, Point> = {};
            for (const kp of pose.keypoints) {
              const name = kp.name ?? '';
              if (!name || (kp.score ?? 0) < .28) continue;
              const x = kp.x / video.videoWidth;
              const y = kp.y / video.videoHeight;
              const prior = this.smoothed[name];
              const next = { x: prior ? prior.x * .62 + x * .38 : x, y: prior ? prior.y * .62 + y * .38 : y, score: kp.score ?? 0, name };
              this.smoothed[name] = next; points[name] = next;
            }
            const confidence = pose.score ?? Object.values(points).reduce((s, p) => s + p.score, 0) / Math.max(1, Object.keys(points).length);
            onPose({ points, confidence, timestamp: performance.now() }, { inferenceFps: 1000 / this.interval, confidence, slow: this.interval >= 100 });
          }
        } catch { onPose(null, { inferenceFps: 0, confidence: 0, slow: true }); }
        this.lastInfer = now;
      }
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }

  stop() {
    this.running = false;
    this.runId++;
    this.stream?.getTracks().forEach(track => track.stop());
    this.stream = null;
  }

  stopInference() { this.running = false; this.runId++; }

  dispose() { this.stop(); this.detector?.dispose(); this.detector = null; }
}
